// Atlassian Connect context and iframe integration
declare global {
  interface Window {
    AP?: {
      context: {
        getContext: (callback: (context: ConnectContext) => void) => void;
        getToken: (callback: (token: string) => void) => void;
      };
      request: {
        get: (url: string, callback?: (data: any) => void) => void;
        post: (url: string, data?: any, callback?: (data: any) => void) => void;
      };
      resize: (width?: string | number, height?: string | number) => void;
      getLocation: (callback: (location: string) => void) => void;
      history: {
        back: () => void;
        forward: () => void;
        go: (delta: number) => void;
        pushState: (data: any, title: string, url?: string) => void;
        replaceState: (data: any, title: string, url?: string) => void;
      };
      dialog: {
        close: (data?: any) => void;
        getButton: (identifier: string) => any;
        isCloseOnEscape: () => boolean;
      };
    };
  }
}

export interface ConnectContext {
  clientKey: string;
  userKey: string;
  userAccountId: string;
  project?: {
    key: string;
    id: string;
    name: string;
  };
  issue?: {
    key: string;
    id: string;
  };
  board?: {
    id: string;
    name: string;
  };
  sprint?: {
    id: string;
    name: string;
  };
}

export interface ConnectInstallation {
  baseUrl: string;
  clientKey: string;
  productType: string;
}

export class ConnectContextService {
  private context: ConnectContext | null = null;
  private installation: ConnectInstallation | null = null;
  private isInIframe: boolean = false;

  constructor() {
    this.isInIframe = window.self !== window.top;
    this.loadConnectAPI();
  }

  private async loadConnectAPI(): Promise<void> {
    if (!this.isInIframe) {
      console.log('Not running in Atlassian Connect iframe');
      return;
    }

    // Wait for Atlassian Connect API to be available
    const checkForAP = () => {
      return new Promise<void>((resolve) => {
        const check = () => {
          if (window.AP) {
            resolve();
          } else {
            setTimeout(check, 100);
          }
        };
        check();
      });
    };

    try {
      await checkForAP();
      console.log('Atlassian Connect API loaded');
      
      // Get context and installation data
      await Promise.all([
        this.loadContext(),
        this.loadInstallation()
      ]);
    } catch (error) {
      console.error('Failed to load Atlassian Connect context:', error);
    }
  }

  private loadContext(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.AP?.context?.getContext) {
        reject(new Error('AP.context.getContext not available'));
        return;
      }

      window.AP.context.getContext((context: ConnectContext) => {
        this.context = context;
        console.log('Connect context loaded:', context);
        resolve();
      });
    });
  }

  private async loadInstallation(): Promise<void> {
    try {
      const response = await fetch('/api/context');
      const data = await response.json();
      this.installation = data.installation;
      console.log('Installation data loaded:', this.installation);
    } catch (error) {
      console.error('Failed to load installation data:', error);
      throw error;
    }
  }

  public getContext(): ConnectContext | null {
    return this.context;
  }

  public getInstallation(): ConnectInstallation | null {
    return this.installation;
  }

  public isConnectEnvironment(): boolean {
    return this.isInIframe && !!window.AP;
  }

  public getJiraBaseUrl(): string | null {
    return this.installation?.baseUrl || null;
  }

  public getCurrentProjectKey(): string | null {
    return this.context?.project?.key || null;
  }

  public getCurrentIssueKey(): string | null {
    return this.context?.issue?.key || null;
  }

  public getUserAccountId(): string | null {
    return this.context?.userAccountId || null;
  }

  // Resize the iframe to fit content
  public resizeIframe(width?: string | number, height?: string | number): void {
    if (window.AP?.resize) {
      window.AP.resize(width, height);
    }
  }

  // Make authenticated requests through the Connect API
  public async makeRequest(method: 'GET' | 'POST', url: string, data?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!window.AP?.request) {
        reject(new Error('AP.request not available'));
        return;
      }

      const callback = (response: any) => {
        if (response.error) {
          reject(new Error(response.error));
        } else {
          resolve(response);
        }
      };

      if (method === 'GET') {
        window.AP.request.get(url, callback);
      } else if (method === 'POST') {
        window.AP.request.post(url, data, callback);
      }
    });
  }

  // Navigate within the Connect environment
  public navigate(url: string): void {
    if (window.AP?.history?.pushState) {
      window.AP.history.pushState({}, '', url);
    } else {
      window.location.href = url;
    }
  }

  // Close dialog if in dialog context
  public closeDialog(data?: any): void {
    if (window.AP?.dialog?.close) {
      window.AP.dialog.close(data);
    }
  }
}

// Global instance
export const connectContext = new ConnectContextService();